import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck, Coins, Droplets, ShieldCheck, UserMinus } from 'lucide-react';
import { adminAPI, profitSharingAPI, stewardAPI, unitAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { hasAdminPermission } from '../utils/adminPermissions';
import {
  calculateComplianceRate,
  extractPhotoUrls,
  getInspectionDisplayOverallStatus,
  getDaysElapsedInMonth,
  getUserDisplayName,
  getUserPhone,
  isSameMonth
} from '../utils/operations';
import {
  formatLocalizedCurrency,
  formatLocalizedDateTime,
  formatLocalizedNumber,
  getInspectionFreeText
} from '../utils/i18nFormatters';

const getReviewBadgeClass = (status) => {
  switch (status) {
    case 'approved':
      return 'bg-emerald-400/10 text-emerald-300';
    case 'rejected':
      return 'bg-rose-400/10 text-rose-300';
    case 'pending_review':
      return 'bg-amber-400/10 text-amber-300';
    default:
      return 'bg-slate-700 text-slate-300';
  }
};

const getReviewBadgeLabel = (t, status) => t(`stewardDetail.reviewStatus.${status || 'unreviewed'}`, {
  defaultValue: t('stewardDetail.reviewStatus.unreviewed')
});

const getOverallBadgeClass = (status) => {
  if (status === 'abnormal') return 'bg-rose-400/10 text-rose-300';
  if (status === 'normal') return 'bg-emerald-400/10 text-emerald-300';
  return 'bg-slate-700 text-slate-300';
};

const getOverallBadgeLabel = (t, status) => t(
  status ? `stewardDetail.overallStatus.${status}` : 'stewardDetail.overallStatus.notUploaded',
  { defaultValue: status || t('stewardDetail.overallStatus.notUploaded') }
);

const tabs = ['units', 'checkins', 'revenue'];

function SummaryCard({ label, value, hint = '', valueClassName = '' }) {
  return (
    <div className="admin-card rounded-2xl p-5">
      <div className="admin-text-muted text-[11px] font-bold uppercase tracking-[0.2em]">{label}</div>
      <div className={`admin-text-primary mt-3 text-3xl font-black ${valueClassName}`}>{value}</div>
      {hint ? <div className="admin-text-secondary mt-1 text-xs">{hint}</div> : null}
    </div>
  );
}

export default function StewardDetail() {
  const { t, i18n } = useTranslation();
  const currentAdmin = useAuthStore((state) => state.user);
  const canManagePartnerBindings = hasAdminPermission(currentAdmin, 'manage_partner_bindings');
  const canViewPartnerNetwork = hasAdminPermission(currentAdmin, 'view_partner_network');
  const canReviewCheckins = hasAdminPermission(currentAdmin, 'review_steward_checkins');
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('units');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    loadData();
    // The detail page only needs to refetch when the route id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [usersRes, unitsRes, treeRes, historyRes, complianceRes] = await Promise.all([
        adminAPI.getUsersAllPages({ role: 'Steward' }),
        unitAPI.getUnits(),
        adminAPI.getPartnerTree(),
        canReviewCheckins ? stewardAPI.getCheckInHistory({ limit: 500 }) : Promise.resolve({ data: { data: [] } }),
        canViewPartnerNetwork ? stewardAPI.getComplianceSummary() : Promise.resolve({ data: { data: { stewards: [] } } })
      ]);
      const devicesRes = canViewPartnerNetwork
        ? await profitSharingAPI.getUserDevices({ userId: id, role: 'Steward' })
        : null;

      const steward = (usersRes.data?.data || []).find((entry) => String(entry.id) === String(id));
      if (!steward) {
        const notFoundError = new Error('STEWARD_NOT_FOUND');
        notFoundError.code = 'STEWARD_NOT_FOUND';
        throw notFoundError;
      }

      const managedUnits = (unitsRes.data?.data || []).filter((unit) => String(unit.stewardId) === String(id));
      const partnerTree = treeRes.data?.data || [];
      const rp = partnerTree.find((entry) => (entry.children || []).some((child) => String(child.id) === String(id))) || null;
      const revenue = devicesRes?.data?.data || { devices: [], totalProfit: 0 };
      const allCheckins = historyRes.data?.data || [];
      const checkins = allCheckins.filter((record) => String(record.stewardId) === String(id));
      const monthlyCheckins = checkins.filter((record) => isSameMonth(record.createdAt));
      const compliance = (complianceRes.data?.data?.stewards || []).find((entry) => String(entry.stewardId) === String(id)) || {};

      const salesByUnitId = {};
      (revenue.devices || []).forEach((entry) => {
        salesByUnitId[String(entry.unitId)] = entry;
      });

      setData({
        steward,
        rp,
        managedUnits,
        checkins,
        monthlyCheckins,
        revenue,
        salesByUnitId,
        pendingReviewCount: monthlyCheckins.filter((record) => record.reviewStatus === 'pending_review').length,
        rejectedReviewCount: checkins.filter((record) => record.reviewStatus === 'rejected').length,
        complianceRate: calculateComplianceRate({
          completedCheckins: Number(compliance.completedCheckins ?? monthlyCheckins.length),
          unitCount: managedUnits.length,
          daysElapsed: getDaysElapsedInMonth()
        })
      });
    } catch (err) {
      setError(err.code === 'STEWARD_NOT_FOUND' ? 'notFound' : 'load');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStewardRole = async () => {
    if (!canManagePartnerBindings) return;
    if (!data?.steward) return;
    const name = getUserDisplayName(data.steward);
    if (!window.confirm(t('stewardDetail.removeRole.confirm', { name }))) {
      return;
    }

    setRemoving(true);
    try {
      const res = await adminAPI.removeUserRole(data.steward.id, 'Steward');
      const unboundUnits = res.data?.data?.unboundUnits ?? 0;
      alert(t('stewardDetail.removeRole.success', { count: unboundUnits }));
      navigate('/stewards');
    } catch (_err) {
      alert(t('stewardDetail.removeRole.failed'));
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return <div role="status" className="p-8 text-slate-300">{t('stewardDetail.loading')}</div>;
  }

  if (error || !data) {
    return (
      <div className="space-y-4 p-8 text-white">
        <button onClick={() => navigate('/stewards')} className="inline-flex items-center gap-2 text-sm text-cyan-300">
          <ArrowLeft size={16} /> {t('stewardDetail.backToList')}
        </button>
        <div role="alert" className="rounded-3xl border border-rose-500/30 bg-rose-500/10 p-6 text-rose-200">
          {t(`stewardDetail.errors.${error || 'notFound'}`)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-8 text-white">
      <header className="space-y-6 rounded-[2rem] border border-white/5 bg-slate-800/50 p-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <button onClick={() => navigate('/stewards')} className="mb-4 inline-flex items-center gap-2 text-sm text-cyan-300">
              <ArrowLeft size={16} /> {t('stewardDetail.backToList')}
            </button>
            <h2 className="text-3xl font-black">{getUserDisplayName(data.steward)}</h2>
            <p className="mt-2 text-sm text-slate-400">
              {t('stewardDetail.profileSummary', {
                phone: getUserPhone(data.steward),
                rp: data.rp?.name || t('stewardDetail.unassignedRp'),
                count: formatLocalizedNumber(data.managedUnits.length, i18n.language)
              })}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {canManagePartnerBindings && <button
              type="button"
              onClick={handleRemoveStewardRole}
              disabled={removing}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-black text-rose-700 transition hover:border-rose-300 hover:bg-rose-100 disabled:opacity-60"
            >
              <UserMinus size={16} />
              {removing ? t('stewardDetail.removeRole.processing') : t('stewardDetail.removeRole.action')}
            </button>}
            {canReviewCheckins && <Link
              to="/steward-checkins"
              className="inline-flex items-center justify-center rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-2 text-sm font-bold text-cyan-200 transition hover:bg-cyan-400 hover:text-slate-950"
            >
              {t('stewardDetail.actions.viewAllCheckins')}
            </Link>}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {canViewPartnerNetwork && <SummaryCard label={t('stewardDetail.summary.complianceRate')} value={`${formatLocalizedNumber(data.complianceRate, i18n.language)}%`} valueClassName="text-emerald-300" />}
          {canViewPartnerNetwork && <SummaryCard label={t('stewardDetail.summary.monthSales')} value={formatLocalizedCurrency(data.revenue.totalRevenue || 0, i18n.language)} />}
          {canViewPartnerNetwork && <SummaryCard label={t('stewardDetail.summary.monthProfit')} value={formatLocalizedCurrency(data.revenue.totalProfit || 0, i18n.language)} valueClassName="text-amber-300" />}
        </div>

      </header>

      {canReviewCheckins && <section className="grid gap-4 md:grid-cols-2">
        <SummaryCard label={t('stewardDetail.summary.pendingReview')} value={formatLocalizedNumber(data.pendingReviewCount, i18n.language)} hint={t('stewardDetail.summary.pendingReviewHint')} valueClassName="text-amber-300" />
        <SummaryCard label={t('stewardDetail.summary.rejectedReview')} value={formatLocalizedNumber(data.rejectedReviewCount, i18n.language)} hint={t('stewardDetail.summary.rejectedReviewHint')} valueClassName="text-rose-300" />
      </section>}

      <div className="flex flex-wrap gap-2 border-b border-white/5 pb-4">
        {(canViewPartnerNetwork ? tabs : tabs.filter((tab) => tab !== 'revenue')).filter((tab) => canReviewCheckins || tab !== 'checkins').map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            aria-pressed={activeTab === tab}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              activeTab === tab ? 'bg-cyan-400 text-slate-950' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {t(`stewardDetail.tabs.${tab}`)}
          </button>
        ))}
      </div>

      {activeTab === 'units' && (
        <section className="grid gap-5 lg:grid-cols-2">
          {data.managedUnits.map((unit) => {
            const sales = data.salesByUnitId[String(unit.id)] || {};
            return (
              <article key={unit.id} className="rounded-[2rem] border border-white/5 bg-slate-800/50 p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-bold">{unit.deviceId}</h3>
                    <p className="mt-1 text-sm text-slate-400">{unit.location || t('stewardDetail.units.locationUnset')}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${unit.status === 'Online' ? 'bg-emerald-400/10 text-emerald-300' : 'bg-slate-700 text-slate-300'}`}>
                    {t(`units.status.${unit.status}`, { defaultValue: unit.status || t('units.status.Unknown') })}
                  </span>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  {canViewPartnerNetwork && <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
                      <Droplets size={14} /> {t('stewardDetail.units.monthSales')}
                    </div>
                    <div className="mt-2 text-xl font-black">{formatLocalizedCurrency(sales.totalRevenue || 0, i18n.language)}</div>
                    <div className="mt-1 text-xs text-slate-400">{t('stewardDetail.units.waterVolume', { value: formatLocalizedNumber(sales.totalVolume || 0, i18n.language) })}</div>
                  </div>}

                  {canViewPartnerNetwork && <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
                      <Coins size={14} /> {t('stewardDetail.units.stewardProfit')}
                    </div>
                    <div className="mt-2 text-xl font-black text-amber-300">{formatLocalizedCurrency(sales.stewardProfit || 0, i18n.language)}</div>
                    <div className="mt-1 text-xs text-slate-400">{t('stewardDetail.units.filterDataPending')}</div>
                  </div>}

                  <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
                      <ShieldCheck size={14} /> {t('stewardDetail.units.waterQuality')}
                    </div>
                    <div className="mt-2 text-sm font-semibold text-white">
                      {t('stewardDetail.units.waterQualityValues', { tds: unit.tdsValue ?? '-', temperature: unit.temperature ?? '-' })}
                    </div>
                    <div className="mt-1 text-xs text-slate-400">{t('stewardDetail.units.phDataPending')}</div>
                  </div>

                  <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
                      <ClipboardCheck size={14} /> {t('stewardDetail.units.recentMaintenance')}
                    </div>
                    <div className="mt-2 text-sm font-semibold text-white">{formatLocalizedDateTime(unit.lastMaintenanceAt, i18n.language)}</div>
                    <div className="mt-1 text-xs text-slate-400">{t('stewardDetail.units.lastHeartbeat', { value: formatLocalizedDateTime(unit.lastHeartbeatAt, i18n.language) })}</div>
                  </div>
                </div>
              </article>
            );
          })}

          {data.managedUnits.length === 0 && (
            <div className="rounded-3xl border border-white/5 bg-slate-800/50 p-12 text-center text-slate-400">{t('stewardDetail.units.empty')}</div>
          )}
        </section>
      )}

      {canReviewCheckins && activeTab === 'checkins' && (
        <section className="space-y-4">
          {data.checkins.map((record) => {
            const photoUrls = extractPhotoUrls(record);
            const overallStatus = getInspectionDisplayOverallStatus(record);

            return (
              <article key={record.id} className="rounded-[2rem] border border-white/5 bg-slate-800/50 p-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="text-lg font-bold text-white">{record.deviceId || t('stewardDetail.checkins.unitFallback', { id: record.unitId })}</div>
                    <div className="mt-1 text-sm text-slate-400">{formatLocalizedDateTime(record.createdAt, i18n.language)}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${getOverallBadgeClass(overallStatus)}`}>
                      {getOverallBadgeLabel(t, overallStatus)}
                    </span>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${getReviewBadgeClass(record.reviewStatus)}`}>
                      {getReviewBadgeLabel(t, record.reviewStatus)}
                    </span>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4 text-sm text-slate-300">
                    {t('stewardDetail.checkins.pureTds')}: <span className="font-semibold text-white">{record.tdsValue ?? '-'}</span>
                  </div>
                  <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4 text-sm text-slate-300">
                    {t('stewardDetail.checkins.ph')}: <span className="font-semibold text-white">{record.phValue ?? '-'}</span>
                  </div>
                  <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4 text-sm text-slate-300">
                    {t('stewardDetail.checkins.cleaning')}: <span className="font-semibold text-white">{t(`stewardDetail.checkins.${record.cleaned ? 'done' : 'notDone'}`)}</span>
                  </div>
                  <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4 text-sm text-slate-300">
                    {t('stewardDetail.checkins.filterLeak')}: <span className="font-semibold text-white">{t(`stewardDetail.checkins.${record.filterChecked ? 'checked' : 'notChecked'}`)} / {t(`stewardDetail.checkins.${record.leakageChecked ? 'checked' : 'notChecked'}`)}</span>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 lg:grid-cols-2">
                  <div className="space-y-3 rounded-2xl border border-white/5 bg-slate-900/30 p-4">
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{t('stewardDetail.checkins.summary')}</div>
                      <div className="mt-2 text-sm text-slate-200">{record.summary ? getInspectionFreeText(t, record.summary) : t('stewardDetail.checkins.summaryEmpty')}</div>
                    </div>
                  </div>

                  <div className="space-y-3 rounded-2xl border border-white/5 bg-slate-900/30 p-4">
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{t('stewardDetail.checkins.reviewNote')}</div>
                      <div className="mt-2 text-sm text-slate-200">{record.reviewNote || t('stewardDetail.checkins.reviewNoteEmpty')}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{t('stewardDetail.checkins.rejectionReason')}</div>
                      <div className="mt-2 text-sm text-rose-200">{record.rejectionReason || t('stewardDetail.checkins.rejectionReasonEmpty')}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{t('stewardDetail.checkins.photos')}</div>
                      <div className="mt-2 flex flex-wrap gap-3">
                        {photoUrls.length > 0 ? photoUrls.map((url) => (
                          <a key={url} href={url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-cyan-300 underline">
                            {t('stewardDetail.checkins.viewPhoto')}
                          </a>
                        )) : (
                          <span className="text-sm text-slate-400">{t('stewardDetail.checkins.noPhotos')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}

          {data.checkins.length === 0 && (
            <div className="rounded-3xl border border-white/5 bg-slate-800/50 p-12 text-center text-slate-400">{t('stewardDetail.checkins.empty')}</div>
          )}
        </section>
      )}

      {canViewPartnerNetwork && activeTab === 'revenue' && (
        <section className="rounded-[2rem] border border-white/5 bg-slate-800/50 p-6">
          <div className="grid gap-4 md:grid-cols-3">
            <SummaryCard label={t('stewardDetail.revenue.monthSales')} value={formatLocalizedCurrency(data.revenue.totalRevenue || 0, i18n.language)} />
            <SummaryCard label={t('stewardDetail.revenue.monthProfit')} value={formatLocalizedCurrency(data.revenue.totalProfit || 0, i18n.language)} valueClassName="text-amber-300" />
            <SummaryCard label={t('stewardDetail.revenue.waterVolume')} value={`${formatLocalizedNumber(data.revenue.totalVolume || 0, i18n.language)} L`} />
          </div>

          <div className="mt-6 overflow-x-auto rounded-2xl border border-white/5">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900/60 text-[11px] uppercase tracking-[0.2em] text-slate-400">
                <tr>
                  <th className="p-4">{t('stewardDetail.revenue.table.device')}</th>
                  <th className="p-4">{t('stewardDetail.revenue.table.sales')}</th>
                  <th className="p-4">{t('stewardDetail.revenue.table.volume')}</th>
                  <th className="p-4">{t('stewardDetail.revenue.table.stewardProfit')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.revenue.devices || []).map((entry) => (
                  <tr key={`${entry.id}-${entry.unitId}`} className="border-t border-white/5">
                    <td className="p-4">
                      <div className="font-semibold text-white">{entry.unit?.deviceId || entry.deviceId || entry.unitId}</div>
                      <div className="text-xs text-slate-400">{entry.unit?.location || entry.unit?.deviceName || t('stewardDetail.units.locationUnset')}</div>
                    </td>
                    <td className="p-4">{formatLocalizedCurrency(entry.totalRevenue || 0, i18n.language)}</td>
                    <td className="p-4">{formatLocalizedNumber(entry.totalVolume || 0, i18n.language)} L</td>
                    <td className="p-4 text-amber-300">{formatLocalizedCurrency(entry.stewardProfit || 0, i18n.language)}</td>
                  </tr>
                ))}
                {(data.revenue.devices || []).length === 0 && (
                  <tr className="border-t border-white/5">
                    <td colSpan={4} className="p-8 text-center text-slate-400">{t('stewardDetail.revenue.empty')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

    </div>
  );
}
